import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { assetTypesApi, type AssetTypeView } from "../api/catalog";
import Ic from "./icons.jsx";

/** Picked type. */
export type AssetTypeRef = { id: string; code: string; name: string };

const PANEL_GAP = 4;
const PANEL_MAX_HEIGHT = 280;

/**
 * Input + first 20 matches from `GET /asset-types?q=`; Escape/outside click
 * closes. Used by the BOM item modal (component type), the position form
 * (expected type) and the template node editor. The result list is rendered
 * in a portal with fixed positioning (same as `Select`) so a scrolling modal
 * or table never clips it; it flips upwards when there is no room below.
 */
export function AssetTypeCombobox({
  value, excludeId, onChange, disabled, placeholder,
}: {
  value: AssetTypeRef | null;
  /** Hidden from results (e.g. the type whose BOM is being edited). */
  excludeId?: string;
  onChange: (v: AssetTypeRef | null) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const { t } = useTranslation();
  const listId = useId();
  const [query, setQuery] = useState(value ? `${value.code} — ${value.name}` : "");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<AssetTypeView[]>([]);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<CSSProperties | null>(null);

  // Anchor the fixed panel to the input; follow scroll/resize of any ancestor.
  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    function place() {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const viewportH = window.innerHeight;
      // The list is a fixed portal: it may run past a modal's edge on purpose (Strahinja:
      // a long list is better than a short scrolling one); only the viewport limits it.
      const below = viewportH - r.bottom - PANEL_GAP;
      const above = r.top - PANEL_GAP;
      const wanted = Math.min(PANEL_MAX_HEIGHT, Math.max(results.length, 1) * 40 + 8);
      const flip = below < wanted && above > below;
      const maxHeight = Math.max(120, Math.min(PANEL_MAX_HEIGHT, flip ? above : below));
      setPos(flip
        ? { position: "fixed", left: r.left, width: r.width, bottom: viewportH - r.top + PANEL_GAP, maxHeight }
        : { position: "fixed", left: r.left, width: r.width, top: r.bottom + PANEL_GAP, maxHeight });
    }
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, results.length]);

  useEffect(() => {
    if (!open) return;
    const h = setTimeout(() => {
      setLoading(true);
      assetTypesApi
        .list({ q: query.trim() || undefined, size: 20, sort: "name,asc" })
        .then((res) => setResults(res.content.filter((x) => x.id !== excludeId)))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(h);
  }, [query, open, excludeId]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      const target = e.target as Node;
      if (wrapRef.current?.contains(target) || listRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  function pick(x: AssetTypeView) {
    onChange({ id: x.id, code: x.code, name: x.name });
    setQuery(`${x.code} — ${x.name}`);
    setOpen(false);
  }

  function clear() {
    onChange(null);
    setQuery("");
    setResults([]);
  }

  return (
    <div ref={wrapRef} className="combo">
      <div style={{ display: "flex", gap: 6 }}>
        <input
          className="input"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listId}
          value={query}
          disabled={disabled}
          placeholder={placeholder ?? t("bom.combo.placeholder", "Pretraži katalog po šifri ili nazivu…")}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActive(0);
            if (value) onChange(null);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive((a) => Math.min(results.length - 1, a + 1)); }
            else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
            else if (e.key === "Enter" && open && results[active]) { e.preventDefault(); pick(results[active]); }
            else if (e.key === "Escape") setOpen(false);
          }}
        />
        {value && !disabled && (
          <button type="button" className="btn" onClick={clear} aria-label={t("action.clear", "Očisti")} title={t("action.clear", "Očisti")}>
            <Ic.x />
          </button>
        )}
      </div>
      {open && !disabled && pos && createPortal(
        <div id={listId} ref={listRef} role="listbox" className="combo-list combo-list-portal dd-scroll" style={pos}>
          {loading && results.length === 0 ? (
            <div className="combo-empty">{t("common.loading", "Učitavanje…")}</div>
          ) : results.length === 0 ? (
            <div className="combo-empty">{t("bom.combo.empty", "Nema rezultata.")}</div>
          ) : (
            results.map((x, i) => (
              <button
                key={x.id}
                type="button"
                role="option"
                aria-selected={value?.id === x.id}
                className={`combo-opt${i === active ? " is-active" : ""}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(x)}
              >
                <span className="code">{x.code}</span>
                <span style={{ flex: 1 }}>{x.name}</span>
              </button>
            ))
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
